import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Permissions} from 'auth/PermissionConstants';
import BufferOptionsItem from 'buffers/BufferOptionsItem';
import {getInsufficientPermissionsToEditMessage} from 'folder-items/FileUtil';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {IsContainedInComplexPolymerData} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {areAaPartFeaturesEnabledForAntibody} from 'molbio/aa-sequences/util/aminoAcidPartsUtil';
import {
  BiointelligentLinkContainerCountQuery,
  BiointelligentLinkContainerCountQueryVariables,
} from 'molbio/biointelligent-links/__generated/BiointelligentLinkContainerCountQuery';
import {BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY} from 'molbio/biointelligent-links/BiointelligentLinkQueries';
import AntibodyToolsMetadata from 'molbio/maps/options/AntibodyToolsMetadata';
import OptionsBanner from 'molbio/maps/options/OptionsBanner';
import SearchButton from 'molbio/maps/options/SearchButton';
import {ZoomSlider, ZoomSliderProps} from 'molbio/maps/options/Sliders';
import DisplayOptions from 'settings/visibility-options/DisplayOptions';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import getTestProps from 'test/lib/cypress/getTestProps';

interface AaSequenceEditOptionsBannerProps {
  proteinId: string;
  isLatest: boolean;
  allowResidueEdits: boolean;
  isArchived: boolean;
  isContainedInComplexPolymerData: IsContainedInComplexPolymerData | null;
  allowFeatureEdits: boolean;
  permissions: Permissions;
}

function AaSequenceEditOptionsBanner({
  proteinId,
  isLatest,
  allowResidueEdits,
  isArchived,
  isContainedInComplexPolymerData,
  allowFeatureEdits,
  permissions,
}: AaSequenceEditOptionsBannerProps): JSX.Element {
  let bannerText: string | null = null;
  const queryResult = useSkippableBenchlingQuery<
    BiointelligentLinkContainerCountQuery,
    BiointelligentLinkContainerCountQueryVariables
  >(BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY, {
    skip: !areAaPartFeaturesEnabledForAntibody(),
    variables: {id: proteinId},
  });
  const isLinkedSequence =
    queryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(queryResult.data.polymer) &&
    queryResult.data.polymer.__typename === 'AaSequenceNode' &&
    queryResult.data.polymer.partContainerCount > 0;

  if (!isLatest) {
    bannerText = 'Editing disabled on previous versions.';
  } else if (!allowResidueEdits) {
    if (isArchived) {
      bannerText = 'Editing disabled on archived AA sequence.';
    } else if (isContainedInComplexPolymerData != null && isContainedInComplexPolymerData.isContained) {
      bannerText = `Editing residues is disabled because AA sequence ${isContainedInComplexPolymerData.errorMessage!}`;
    } else if (isLinkedSequence) {
      bannerText = 'Editing is disabled on linked sequences.';
    } else if (allowFeatureEdits || permissions.writable) {
      bannerText = 'Editing residues is disabled due to insufficient permissions.';
    } else {
      bannerText = getInsufficientPermissionsToEditMessage(permissions, 'AA sequence');
    }
  }
  return <OptionsBanner bannerText={bannerText} />;
}

export interface AaSequenceMapOptionsProps extends AaSequenceEditOptionsBannerProps {
  aaSequenceVersionId: number;
  aminoAcids: string;
  onSearch: () => void;
  visibilityOptions: VisibilityOptions;
  zoomSliderProps: ZoomSliderProps;
  featureCounts: {annotations: number; parts: number};
  proteinId: string;
}

function AaSequenceMapOptions({
  aaSequenceVersionId,
  aminoAcids,
  onSearch,
  visibilityOptions,
  zoomSliderProps,
  featureCounts,
  proteinId,
  isLatest,
  allowResidueEdits,
  isArchived,
  isContainedInComplexPolymerData,
  allowFeatureEdits,
  permissions,
}: AaSequenceMapOptionsProps): JSX.Element {
  return (
    <div className="reset">
      <div
        className="buffer-options-container"
        {...getTestProps({
          component: 'AaSequenceMapOptions',
          element: 'root',
        })}
      >
        <BufferOptionsItem>
          <ZoomSlider {...zoomSliderProps} className={css(styles.noShrink)} />
        </BufferOptionsItem>
        <AaSequenceEditOptionsBanner
          proteinId={proteinId}
          isLatest={isLatest}
          allowResidueEdits={allowResidueEdits}
          isArchived={isArchived}
          isContainedInComplexPolymerData={isContainedInComplexPolymerData}
          allowFeatureEdits={allowFeatureEdits}
          permissions={permissions}
        />
        <AntibodyToolsMetadata
          aaSequenceId={proteinId}
          aaSequenceVersionId={aaSequenceVersionId}
          aminoAcids={aminoAcids}
          visibilityOptions={visibilityOptions}
        />
        <div className={`buttonGroup ${css(styles.noShrink)}`}>
          <DisplayOptions dropLeft={true} featureCounts={featureCounts} options={visibilityOptions} />
          <SearchButton onToggleSearch={onSearch} />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  noShrink: {
    flexShrink: 0,
  },
});

export default AaSequenceMapOptions;
